import { useState, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiCheck, FiX, FiUser } from 'react-icons/fi';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { resolveDispute } from '../../hooks/useAdmin';
import { useBounty } from '../../hooks/useBounties';
import { useClaim } from '../../hooks/useClaims';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { StatusBadge } from '../common/StatusBadge';
import { ReputationBadge } from '../common/ReputationBadge';
import { formatDate } from '../../utils/formatters';
import { Price } from '../common/Price';

export default function AdminDisputeDetail() {
  const { arbitrationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const approveModal = useDisclosure();
  const rejectModal = useDisclosure();

  const [arb, setArb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [rating, setRating] = useState(3);
  const [bountyCreator, setBountyCreator] = useState(null);
  const [claimant, setClaimant] = useState(null);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  useEffect(() => {
    if (!arbitrationId) return;
    const unsubscribe = onSnapshot(doc(db, 'arbitration', arbitrationId), (snap) => {
      setArb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsubscribe;
  }, [arbitrationId]);

  const { bounty } = useBounty(arb?.bountyId);
  const { claim } = useClaim(arb?.claimId);

  // Fetch bounty creator profile
  useEffect(() => {
    if (!bounty?.creatorUid) { setBountyCreator(null); return; }
    const unsubscribe = onSnapshot(doc(db, 'users', bounty.creatorUid), (snap) => {
      setBountyCreator(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return unsubscribe;
  }, [bounty?.creatorUid]);

  // Fetch claimant profile
  useEffect(() => {
    if (!claim?.claimantUid) { setClaimant(null); return; }
    const unsubscribe = onSnapshot(doc(db, 'users', claim.claimantUid), (snap) => {
      setClaimant(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return unsubscribe;
  }, [claim?.claimantUid]);

  const handleResolve = async (resolution) => {
    setResolving(true);
    try {
      await resolveDispute(arbitrationId, resolution, arb.claimId, arb.bountyId, rating);
      toast({
        title: `Dispute resolved — submission ${resolution === 'approve' ? 'approved' : 'rejected'}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      navigate('/admin/disputes');
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setResolving(false);
      approveModal.onClose();
      rejectModal.onClose();
    }
  };

  if (loading) return <LoadingSpinner message="Loading dispute..." />;

  if (!arb) {
    return (
      <VStack spacing={4} py={8}>
        <Heading size="md">Dispute not found</Heading>
        <Button as={Link} to="/admin/disputes" leftIcon={<Icon as={FiArrowLeft} />}>Back to Disputes</Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Button
        as={Link}
        to="/admin/disputes"
        variant="ghost"
        leftIcon={<Icon as={FiArrowLeft} />}
        alignSelf="flex-start"
        size="sm"
      >
        Back to Disputes
      </Button>

      {/* Arbitration Info */}
      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <Heading size="md">Dispute Details</Heading>
            <StatusBadge status={arb.status} />
          </HStack>
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.500">Dispute Reason</Text>
            <Text mt={1}>{arb.reason || 'No reason provided'}</Text>
          </Box>
          <HStack spacing={6}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Filed</Text>
              <Text fontSize="sm">{formatDate(arb.initiatedAt)}</Text>
            </Box>
            {arb.resolvedAt && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.500">Resolved</Text>
                <Text fontSize="sm">{formatDate(arb.resolvedAt)}</Text>
              </Box>
            )}
          </HStack>
        </VStack>
      </Box>

      {/* Involved Parties */}
      {(bountyCreator || claimant) && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {/* Bounty Creator */}
          {bountyCreator && (
            <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={5}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={3}>
                Task Creator
              </Text>
              <HStack spacing={3} mb={3}>
                <Avatar
                  size="sm"
                  src={bountyCreator.avatarUrl}
                  name={bountyCreator.anonymizedUsername || 'User'}
                  bg="orange.400"
                />
                <Box>
                  <Text fontWeight="medium" fontSize="sm">{bountyCreator.anonymizedUsername || '—'}</Text>
                  <Text fontSize="xs" color="gray.500">{bountyCreator.email || '—'}</Text>
                </Box>
              </HStack>
              <HStack spacing={3} mb={3}>
                {bountyCreator.reputation && <ReputationBadge reputation={bountyCreator.reputation} />}
                {bountyCreator.isAdmin && <Badge colorScheme="purple" fontSize="xs">Admin</Badge>}
                {bountyCreator.isBanned && <Badge colorScheme="red" fontSize="xs">Banned</Badge>}
              </HStack>
              <Button
                as={Link}
                to={`/admin/users/${bountyCreator.id}`}
                size="xs"
                variant="outline"
                leftIcon={<Icon as={FiUser} />}
              >
                View Profile
              </Button>
            </Box>
          )}

          {/* Claimant / Researcher */}
          {claimant && (
            <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={5}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={3}>
                Researcher (Claimant)
              </Text>
              <HStack spacing={3} mb={3}>
                <Avatar
                  size="sm"
                  src={claimant.avatarUrl}
                  name={claimant.anonymizedUsername || 'User'}
                  bg="orange.400"
                />
                <Box>
                  <Text fontWeight="medium" fontSize="sm">{claimant.anonymizedUsername || '—'}</Text>
                  <Text fontSize="xs" color="gray.500">{claimant.email || '—'}</Text>
                </Box>
              </HStack>
              <HStack spacing={3} mb={3}>
                {claimant.reputation && <ReputationBadge reputation={claimant.reputation} />}
                {claimant.isAdmin && <Badge colorScheme="purple" fontSize="xs">Admin</Badge>}
                {claimant.isBanned && <Badge colorScheme="red" fontSize="xs">Banned</Badge>}
              </HStack>
              <Button
                as={Link}
                to={`/admin/users/${claimant.id}`}
                size="xs"
                variant="outline"
                leftIcon={<Icon as={FiUser} />}
              >
                View Profile
              </Button>
            </Box>
          )}
        </SimpleGrid>
      )}

      {/* Bounty Info */}
      {bounty && (
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
          <Heading size="sm" mb={3}>Task</Heading>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text fontWeight="medium">{bounty.title}</Text>
              <StatusBadge status={bounty.status} />
            </HStack>
            <Text fontSize="sm" color="gray.500">
              Created by {bounty.creatorAnonymizedUsername} | Reward: <Price cents={bounty.rewardAmount} size="sm" />
            </Text>
            <Text fontSize="sm" noOfLines={3}>{bounty.description}</Text>
          </VStack>
        </Box>
      )}

      {/* Claim Info */}
      {claim && (
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
          <Heading size="sm" mb={3}>Submission</Heading>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text fontWeight="medium">By {claim.claimantAnonymizedUsername}</Text>
              <StatusBadge status={claim.status} />
            </HStack>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.500">Research Findings</Text>
              <Text fontSize="sm" whiteSpace="pre-wrap" mt={1}>{claim.researchFindings}</Text>
            </Box>
            {claim.sources?.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.500">Sources</Text>
                {claim.sources.map((s, i) => (
                  <Text key={i} fontSize="sm" color="blue.500">{s}</Text>
                ))}
              </Box>
            )}
            {claim.attachmentUrls?.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.500">Attachments</Text>
                <Text fontSize="sm">{claim.attachmentUrls.length} file(s)</Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}

      {/* Resolution Actions */}
      {arb.status === 'open' && (
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6}>
          <Heading size="sm" mb={4}>Resolve Dispute</Heading>
          <HStack spacing={3}>
            <Button
              colorScheme="green"
              leftIcon={<Icon as={FiCheck} />}
              onClick={approveModal.onOpen}
            >
              Approve Submission (Researcher Wins)
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              leftIcon={<Icon as={FiX} />}
              onClick={rejectModal.onOpen}
            >
              Reject Submission (Collector Wins)
            </Button>
          </HStack>
        </Box>
      )}

      {arb.status === 'resolved' && (
        <Alert status="info" rounded="md">
          <AlertIcon />
          This dispute has been resolved. Resolution: submission was {arb.resolution === 'approve' ? 'approved' : 'rejected'}.
        </Alert>
      )}

      {/* Approve Modal */}
      <Modal isOpen={approveModal.isOpen} onClose={approveModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Approve Submission</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              This will approve the researcher's submission, mark the task as completed, and update the researcher's reputation.
            </Text>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Rating for researcher (1-5 stars)</Text>
            <NumberInput value={rating} onChange={(_, v) => setRating(v)} min={1} max={5} maxW="100px">
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={approveModal.onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={() => handleResolve('approve')} isLoading={resolving}>
              Approve
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={rejectModal.isOpen} onClose={rejectModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reject Submission</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              This will reject the researcher's submission. The task will remain open for new submissions. The researcher will be notified.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={rejectModal.onClose}>Cancel</Button>
            <Button colorScheme="red" onClick={() => handleResolve('reject')} isLoading={resolving}>
              Reject
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
